import React from 'react';
import { SafeImg } from '../../SafeImg';
import { Btn, H1, Eyebrow, tidFor, E } from './_shared';

/* ════════════════════════════════════════════════════════════════════════
 * NEW BLOCK TYPE — footer (8 variants from Figma "Footers" page)
 * Variants: minimal-centered · nav-row-social · multi-column-white ·
 *           newsletter-capture · list-compact · cta-bar · slim-copyright ·
 *           multi-column-dark
 *
 * Iter 175 — Section Audit. brand · tagline · copyright · column titles
 * and column links · nav items · cta title/label are all inline-editable
 * when `E.onEdit` is wired through the premium dispatch chain.
 * ════════════════════════════════════════════════════════════════════════ */
export function FooterBlock({ block, theme, E: EProps = {} }) {
  const { onEdit } = EProps;
  const c = block.content || {};
  const layout = block.layout || 'minimal-centered';
  const brand = c.brand || 'Your Brand';
  const tagline = c.tagline || '';
  const year = new Date().getFullYear();
  const copyright = c.copyright || `© ${year} ${brand}. All rights reserved.`;
  const tid = tidFor(block);
  const cols = c.columns || [
    { title: 'Product', links: ['Features', 'Pricing', 'Templates', 'Roadmap'] },
    { title: 'Company', links: ['About', 'Blog', 'Careers', 'Press'] },
    { title: 'Resources', links: ['Help Center', 'Community', 'Contact', 'API'] },
    { title: 'Legal', links: ['Privacy', 'Terms', 'Cookies', 'Licenses'] },
  ];
  const socials = c.socials || ['Twitter', 'Instagram', 'LinkedIn'];

  if (layout === 'minimal-centered') {
    return (
      <footer data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-12 text-center" style={{ background: theme.bg, color: theme.ink }}>
        <E block={block} onEdit={onEdit} field="brand" value={brand} tag="p" className="text-xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="Your Brand" />
        <E block={block} onEdit={onEdit} field="tagline" value={tagline} tag="p" className="mt-2 text-sm opacity-70 max-w-xl mx-auto" placeholder="Tagline" />
        <E block={block} onEdit={onEdit} field="copyright" value={copyright} tag="p" className="mt-6 text-xs opacity-50" placeholder="© Your Brand" />
      </footer>
    );
  }

  if (layout === 'nav-row-social') {
    const nav = c.nav || ['Home', 'About', 'Work', 'Contact'];
    return (
      <footer data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-8" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex gap-6 text-sm font-medium">
            {nav.map((n,i) => (
              <E key={i} block={block} onEdit={onEdit} field={`nav.${i}`} value={n} tag="a" className="opacity-75 hover:opacity-100 cursor-text" placeholder="Nav" />
            ))}
          </div>
          <div className="flex gap-3">
            {socials.map((s,i) => <a key={i} href="#" className="w-8 h-8 rounded-full border border-current/20 flex items-center justify-center text-xs opacity-70 hover:opacity-100">{s[0]}</a>)}
          </div>
        </div>
        <E block={block} onEdit={onEdit} field="copyright" value={copyright} tag="p" className="mt-6 text-center text-xs opacity-50" placeholder="© Your Brand" />
      </footer>
    );
  }

  if (layout === 'multi-column-white' || layout === 'multi-column-dark') {
    const dark = layout === 'multi-column-dark';
    const bg = dark ? '#0E0E23' : theme.bg;
    const ink = dark ? '#fff' : theme.ink;
    return (
      <footer data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-16" style={{ background: bg, color: ink }}>
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-5 gap-8">
          <div className="col-span-2">
            <E block={block} onEdit={onEdit} field="brand" value={brand} tag="p" className="text-2xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="Your Brand" />
            <E block={block} onEdit={onEdit} field="tagline" value={tagline} tag="p" className="mt-3 text-sm opacity-70 max-w-xs" placeholder="Tagline" />
            <div className="mt-5 flex gap-3">
              {socials.map((s,i) => <a key={i} href="#" className="w-9 h-9 rounded-full border border-current/20 flex items-center justify-center text-xs opacity-70 hover:opacity-100">{s[0]}</a>)}
            </div>
          </div>
          {cols.slice(0,3).map((col,i) => (
            <div key={i}>
              <E block={block} onEdit={onEdit} field={`columns.${i}.title`} value={col.title} tag="p" className="text-xs uppercase tracking-[0.18em] opacity-60 font-bold" placeholder="Section" />
              <ul className="mt-3 space-y-2">{col.links.map((l,li) => (
                <li key={li}><E block={block} onEdit={onEdit} field={`columns.${i}.links.${li}`} value={l} tag="a" className="text-sm opacity-80 hover:opacity-100 cursor-text" placeholder="Link" /></li>
              ))}</ul>
            </div>
          ))}
        </div>
        <div className="max-w-6xl mx-auto mt-10 pt-6 border-t border-current/10 flex flex-col sm:flex-row justify-between text-xs opacity-50 gap-2">
          <E block={block} onEdit={onEdit} field="copyright" value={copyright} tag="span" placeholder="© Your Brand" />
          <span>Made with care.</span>
        </div>
      </footer>
    );
  }

  if (layout === 'newsletter-capture') {
    return (
      <footer data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-16" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <E block={block} onEdit={onEdit} field="brand" value={brand} tag="p" className="text-2xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="Your Brand" />
            <E block={block} onEdit={onEdit} field="tagline" value={tagline} tag="p" className="mt-3 text-sm opacity-70 max-w-sm" placeholder="Tagline" />
            <E block={block} onEdit={onEdit} field="newsletter_title" value={c.newsletter_title || 'Join the newsletter'} tag="p" className="mt-6 text-xs uppercase tracking-[0.18em] opacity-60 font-bold" placeholder="Newsletter title" />
            <div className="mt-3 flex gap-2 max-w-sm">
              <input type="email" placeholder="your@email.com" className="flex-1 px-4 py-2.5 border border-current/20 bg-transparent rounded-lg text-sm" />
              <button className="px-5 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider text-white" style={{ background: theme.accent || '#2DD4BF' }}>Subscribe</button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
            {cols.slice(0,3).map((col,i) => (
              <div key={i}>
                <E block={block} onEdit={onEdit} field={`columns.${i}.title`} value={col.title} tag="p" className="text-xs uppercase tracking-[0.18em] opacity-60 font-bold" placeholder="Section" />
                <ul className="mt-3 space-y-2">{col.links.map((l,li) => (
                  <li key={li}><E block={block} onEdit={onEdit} field={`columns.${i}.links.${li}`} value={l} tag="a" className="text-sm opacity-80 hover:opacity-100 cursor-text" placeholder="Link" /></li>
                ))}</ul>
              </div>
            ))}
          </div>
        </div>
        <E block={block} onEdit={onEdit} field="copyright" value={copyright} tag="p" className="max-w-6xl mx-auto mt-10 pt-6 border-t border-current/10 text-xs opacity-50" placeholder="© Your Brand" />
      </footer>
    );
  }

  if (layout === 'cta-bar') {
    return (
      <footer data-block-type={block.type} data-layout={block.layout || "default"} style={{ background: theme.bg, color: theme.ink }}>
        <div className="px-6 py-12 text-center text-white" style={{ background: theme.accent || '#7C3AED' }}>
          <E block={block} onEdit={onEdit} field="cta_title" value={c.cta_title || 'Ready to get started?'} tag="h2" className="text-2xl md:text-3xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="CTA title" />
          <span className="mt-5 inline-block px-8 py-3 rounded-full text-xs font-bold uppercase tracking-wider bg-white text-gray-900 cursor-text">
            <E block={block} onEdit={onEdit} field="cta_label" value={c.cta_label || 'Get Started'} tag="span" placeholder="Button" />
          </span>
        </div>
        <div className="px-6 py-8 max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-3 text-xs opacity-60">
          <span><E block={block} onEdit={onEdit} field="brand" value={brand} tag="span" placeholder="Your Brand" /> · <E block={block} onEdit={onEdit} field="copyright" value={copyright} tag="span" placeholder="© Your Brand" /></span>
          <div className="flex gap-3">{socials.map((s,i) => <a key={i} href="#" className="opacity-70 hover:opacity-100">{s}</a>)}</div>
        </div>
      </footer>
    );
  }

  if (layout === 'slim-copyright') {
    return (
      <footer data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-5 border-t border-current/10" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2 text-xs opacity-60">
          <E block={block} onEdit={onEdit} field="copyright" value={copyright} tag="span" placeholder="© Your Brand" />
          <div className="flex gap-4">{(c.legal_links || ['Privacy', 'Terms', 'Cookies']).map((l,i) => (
            <E key={i} block={block} onEdit={onEdit} field={`legal_links.${i}`} value={l} tag="a" className="hover:opacity-100 cursor-text" placeholder="Link" />
          ))}</div>
        </div>
      </footer>
    );
  }

  // ── mega-menu · Iter 189c Phase B ────────────────────────────────────
  //  Wide 5-column footer with a featured post/product on the left and
  //  4 navigation columns on the right. Bottom bar has social + copyright.
  if (layout === 'mega-menu') {
    const featured = c.featured || {
      eyebrow: 'FROM THE JOURNAL',
      title: 'The state of independent commerce · 2026',
      image_url: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=600&q=80',
      link_text: 'Read the report →',
    };
    return (
      <footer data-block-type={block.type} data-layout={block.layout || 'default'} className="px-6 pt-16 pb-8" style={{ background: theme.deep || '#0E0E23', color: '#fff' }}>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Featured card */}
          <div className="md:col-span-4">
            <E block={block} onEdit={onEdit} field="brand" value={brand} tag="p" className="text-2xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="Your Brand" />
            <E block={block} onEdit={onEdit} field="tagline" value={tagline || 'Making the tools we wish existed.'} tag="p" className="mt-2 text-sm opacity-70 max-w-xs" placeholder="Tagline" />

            <div className="mt-6 rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
              {featured.image_url && <SafeImg src={featured.image_url} alt="" className="w-full aspect-[16/10] object-cover" />}
              <div className="p-4">
                <E block={block} onEdit={onEdit} field="featured.eyebrow" value={featured.eyebrow} tag="p" className="text-[10px] uppercase tracking-[0.24em] font-bold" style={{ color: theme.accent || '#8B5CF6' }} placeholder="Eyebrow" />
                <E block={block} onEdit={onEdit} field="featured.title" value={featured.title} tag="p" className="mt-2 text-sm font-bold leading-snug" style={{ fontFamily: theme.headingFont }} placeholder="Featured title" />
                <E block={block} onEdit={onEdit} field="featured.link_text" value={featured.link_text} tag="a" className="mt-3 inline-block text-xs font-bold uppercase tracking-wider cursor-text" style={{ color: theme.accent || '#8B5CF6' }} placeholder="Read more →" />
              </div>
            </div>
          </div>

          {/* Nav columns */}
          <div className="md:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8">
            {cols.map((col, i) => (
              <div key={i}>
                <E block={block} onEdit={onEdit} field={`columns.${i}.title`} value={col.title} tag="p" className="text-xs uppercase tracking-[0.22em] font-bold opacity-70" placeholder="Section" />
                <ul className="mt-4 space-y-2.5">
                  {col.links.map((l, li) => (
                    <li key={li}>
                      <E block={block} onEdit={onEdit} field={`columns.${i}.links.${li}`} value={l} tag="a" className="text-sm opacity-80 hover:opacity-100 cursor-text" placeholder="Link" />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="max-w-6xl mx-auto mt-14 pt-6 flex flex-col md:flex-row items-center justify-between gap-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <E block={block} onEdit={onEdit} field="copyright" value={copyright} tag="p" className="text-xs opacity-50" placeholder="© Your Brand" />
          <div className="flex items-center gap-4">
            {socials.map((s, i) => (
              <E key={i} block={block} onEdit={onEdit} field={`socials.${i}`} value={s} tag="a" className="text-xs uppercase tracking-wider opacity-70 hover:opacity-100 cursor-text" placeholder="Social" />
            ))}
          </div>
        </div>
      </footer>
    );
  }

  // list-compact — default
  return (
    <footer data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-12" style={{ background: theme.bg, color: theme.ink }}>
      <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
        <div>
          <E block={block} onEdit={onEdit} field="brand" value={brand} tag="p" className="text-lg font-bold" style={{ fontFamily: theme.headingFont }} placeholder="Your Brand" />
          <E block={block} onEdit={onEdit} field="tagline" value={tagline} tag="p" className="mt-2 text-xs opacity-70" placeholder="Tagline" />
        </div>
        {cols.slice(0,3).map((col,i) => (
          <div key={i}>
            <E block={block} onEdit={onEdit} field={`columns.${i}.title`} value={col.title} tag="p" className="text-xs uppercase tracking-[0.18em] opacity-60 font-bold" placeholder="Section" />
            <ul className="mt-2 space-y-1">{col.links.map((l,li) => (
              <li key={li}><E block={block} onEdit={onEdit} field={`columns.${i}.links.${li}`} value={l} tag="a" className="text-xs opacity-80 hover:opacity-100 cursor-text" placeholder="Link" /></li>
            ))}</ul>
          </div>
        ))}
      </div>
      <E block={block} onEdit={onEdit} field="copyright" value={copyright} tag="p" className="max-w-6xl mx-auto mt-8 pt-4 border-t border-current/10 text-xs opacity-50" placeholder="© Your Brand" />
    </footer>
  );
}
